import {
  current,
  currentDate,
  endPoints,
  expiring,
  inventoryTypes,
  leadStatusObj,
  leadTypes,
  pending_Status,
  saleCardVariantObj,
  targetTaskStatusOptionsObj,
  taskStatusObj,
  tokenStatusObj,
  yesterdayDate,
} from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { dashboardCardState, defaultMetaObj } from "store/constants";
import { cardTypes, paramsObjForCards } from "pages/dashboard/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { createAsyncThunk, PayloadAction, createSlice } from "@reduxjs/toolkit";

const {
  leadEndPoint,
  leadTaskEndPoint,
  tokenEndPoint,
  inventoryEndPoint,
  individualPropertyEndPoint,
  staffTargetEndPoint,
  salesEndPoint,
} = endPoints;
const { lead, task, token, inventory, target, sales } = cardTypes;
const { rawLead, polishedLead } = leadTypes;
const { projectInventory, nonProjectInventory } = inventoryTypes;
const { taskStatusOpen, taskStatusPending } = taskStatusObj;
const { statusInProgress, statusStopped, statusNotStarted } =
  targetTaskStatusOptionsObj;
const { tokenStatusOpen, tokenStatusExtended } = tokenStatusObj;

const { statusActive, statusNew, statusRecycled, tokenBooked, tokenExtended } =
  leadStatusObj;

function getEndPoint({ type }: any) {
  switch (type) {
    case lead:
      return leadEndPoint;
    case task:
      return leadTaskEndPoint;
    case token:
      return tokenEndPoint;
    case inventory:
      return inventoryEndPoint;
    case projectInventory:
      return inventoryEndPoint;
    case nonProjectInventory:
      return individualPropertyEndPoint;
    case target:
      return staffTargetEndPoint;
    case sales:
      return salesEndPoint;

    default:
      return leadEndPoint;
  }
}

export const dashboardCardbasedOnKeys: {
  [key: string]: any;
} = {
  [cardTypes.task]: {
    "Created Date": "date_created",
    "Close Date": "date_closed",
    "Due Date": "due_date",
    "Updated Date": "date_updated",
  },
  [cardTypes.lead]: {
    "Created Date": "date_open",
    "Close Date": "date_closed",
    "Updated Date": "date_updated",
    "Due Date": "followup_date",
  },
  [cardTypes.token]: {
    "Created Date": "date_created",
    "Expiry Date": "date_expiry",
    "Updated Date": "date_updated",
  },
  [cardTypes.inventory]: {
    "Created Date": "date_created",
    "Updated Date": "date_updated",
  },
  [cardTypes.target]: {
    "Created Date": "date_created",
    "Due Date": "due_date",
    "Updated Date": "date_updated",
  },
  [cardTypes.sales]: {
    "Created Date": "date_created",
  },
};

function getDateRange({ type, value, basedOn }: any) {
  const getBasedOnKey = dashboardCardbasedOnKeys[type]?.[basedOn];
  switch (type) {
    case lead:
      if (value.filter(Boolean).length === 2) {
        return {
          [`${getBasedOnKey}__lte`]: value[1],
          [`${getBasedOnKey}__gte`]: value[0],
        };
      } else if (value.filter(Boolean).length === 1) {
        return {
          [`${getBasedOnKey}__lte`]: value[1],
        };
      } else {
        return {};
      }
    case token:
    case task:
    case inventory:
    case target:
    case sales:
      if (value.filter(Boolean).length === 2) {
        return {
          [`${getBasedOnKey}__lte`]: value[1],
          [`${getBasedOnKey}__gte`]: value[0],
        };
      } else if (value.filter(Boolean).length === 1) {
        return {
          [`${getBasedOnKey}__lte`]: value[1],
        };
      } else {
        return {};
      }

    default:
      break;
  }
}

function getDateParamsConditionallyWithExtraParam({
  type,
  status,
  dateParams,
}: any) {
  if (status === expiring && type === lead) {
    const todaysDateParams = getDateRange({
      type,
      value: [currentDate, currentDate],
      basedOn: "Due Date",
    });

    return {
      ...todaysDateParams,
      lead_status__in: `${statusNew},${statusActive},${statusRecycled},${tokenBooked},${tokenExtended}`,
    };
  }

  if (status === pending_Status && type === lead) {
    const todaysDateParams = getDateRange({
      type,
      value: [null, yesterdayDate],
      basedOn: "Due Date",
    });

    return {
      ...todaysDateParams,
      lead_status__in: `${statusNew},${statusActive},${statusRecycled},${tokenBooked},${tokenExtended}`,
    };
  }

  if (status === current && type === task) {
    const todaysDateParams = getDateRange({
      type,
      value: [currentDate, currentDate],
      basedOn: "Due Date",
    });
    return {
      ...todaysDateParams,
      task_status__in: `${taskStatusOpen},${taskStatusPending}`,
    };
  }
  if (status === current && type === target) {
    const todaysDateParams = getDateRange({
      type,
      value: [currentDate, currentDate],
      basedOn: "Due Date",
    });
    return {
      ...todaysDateParams,
      status__in: `${statusInProgress},${statusStopped},${statusNotStarted}`,
    };
  }

  if (status === pending_Status && type === target) {
    const todaysDateParams = getDateRange({
      type,
      value: [null, yesterdayDate],
      basedOn: "Due Date",
    });
    return {
      ...todaysDateParams,
      status__in: `${statusInProgress},${statusStopped},${statusNotStarted}`,
    };
  }

  if (status === expiring && type === token) {
    const todaysDateParams = getDateRange({
      type,
      value: [currentDate, currentDate],
      basedOn: "Expiry Date",
    });

    return {
      ...todaysDateParams,
      token_status__in: `${tokenStatusOpen},${tokenStatusExtended}`,
    };
  }

  return dateParams;
}

export const fetchAllDashboardCardsData = createAsyncThunk(
  "cardsData/fetchAllDashboardCardsData",
  async (
    { data, dateRange }: { data: any[]; dateRange: any },
    { rejectWithValue }
  ) => {
    try {
      // have to pass search params for non admin users
      const allPromises = data.map(async (keyToExtract: any) => {
        const { type, title, status, priority, based_on } = keyToExtract || {};

        try {
          const url = getEndPoint({ type });
          const searchParams =
            type === cardTypes.sales
              ? {
                  ...paramsObjForCards[type]({
                    variant: based_on,
                    user: status,
                    project: priority,
                  }),
                }
              : {
                  ...(paramsObjForCards[type]?.[status]
                    ? paramsObjForCards[type]?.[status]
                    : {}),
                  ...(paramsObjForCards[type]?.[priority]
                    ? paramsObjForCards[type]?.[priority]
                    : {}),
                };

          // paramsObjForCards[type][status ? status : priority] || {};
          const dateParams = getDateRange({
            type,
            value: dateRange,
            basedOn: "Created Date",
          });

          const conditionalDateParams =
            getDateParamsConditionallyWithExtraParam({
              type,
              status,
              dateParams,
            });

          if (type === inventory) {
            const paginatedUrlInventory = getPaginatedUrlWithSearchParams({
              pageNumber: 1,
              endpoint: inventoryEndPoint,
              limit: 1,
              searchParams: {
                ...searchParams,
                ...dateParams,
              },
            });

            const paginatedUrlNonProjectInventory =
              getPaginatedUrlWithSearchParams({
                pageNumber: 1,
                endpoint: individualPropertyEndPoint,
                limit: 1,
                searchParams: {
                  ...searchParams,
                  ...dateParams,
                },
              });

            const resultProjectInventory: any = await crudService.getAll(
              paginatedUrlInventory
            );

            const resultNonProjectInventory: any = await crudService.getAll(
              paginatedUrlNonProjectInventory
            );

            return {
              count:
                resultNonProjectInventory?.data?.meta?.total_count +
                resultProjectInventory?.data?.meta?.total_count,
              ...keyToExtract,
            };
          }

          const paginatedUrl = getPaginatedUrlWithSearchParams({
            pageNumber: 1,
            endpoint: url,
            limit: 1,
            searchParams: {
              ...searchParams,
              ...conditionalDateParams,
              // ...todaysDateParams,
              // ...dateParams,
            },
          });

          const result: any = await crudService.getAll(paginatedUrl);

          const totolSales =
            type === cardTypes.sales &&
            based_on === saleCardVariantObj.saleAmount
              ? {
                  total_sales: result?.data?.objects[0]?.total_sales,
                }
              : {};

          return {
            count: result?.data?.meta?.total_count,
            ...totolSales,
            ...keyToExtract,
          };
        } catch (error) {
          return {
            count: 0,
            ...keyToExtract,
          };
        }
      });

      return await Promise.all(allPromises);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchLeadsDashboardCardsData = createAsyncThunk(
  "cardsData/fetchLeadsDashboardCardsData",
  async (
    {
      data,

      dateRange,
    }: { data: []; dateRange: any },
    { rejectWithValue }
  ) => {
    try {
      // have to pass search params for non admin users
      const allPromises = data.map(async (keyToExtract: any) => {
        const { type, title, status, priority } = keyToExtract || {};

        const url = getEndPoint({ type: lead });

        const searchParams = {
          ...(paramsObjForCards[lead]?.[status]
            ? paramsObjForCards[lead]?.[status]
            : {}),
          ...(paramsObjForCards[lead]?.[priority]
            ? paramsObjForCards[lead]?.[priority]
            : {}),
        };

        const dateParams = getDateRange({
          type: lead,
          value: dateRange,
          basedOn: "Created Date",
        });

        const conditionalDateParams =
          status === expiring || status === pending_Status
            ? getDateParamsConditionallyWithExtraParam({
                type: lead,
                status,
                dateParams,
              })
            : {};

        const paginatedUrl = getPaginatedUrlWithSearchParams({
          pageNumber: 1,
          endpoint: url,
          limit: 1,
          searchParams: {
            ...searchParams,
            ...dateParams,
            ...conditionalDateParams,
            is_polish_lead: type === polishedLead,
          },
        });

        const result: any = await crudService.getAll(paginatedUrl);
        return {
          count: result?.data?.meta?.total_count,
          ...keyToExtract,
        };
      });

      return await Promise.all(allPromises);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchInventoryDashboardCardsData = createAsyncThunk(
  "cardsData/fetchInventoryDashboardCardsData",
  async (
    {
      data,

      dateRange,
    }: { data: []; dateRange: any },
    { rejectWithValue }
  ) => {
    try {
      // have to pass search params for non admin users
      const allPromises = data.map(async (keyToExtract: any) => {
        const { type, title, status, priority } = keyToExtract || {};

        const url = getEndPoint({ type });

        const searchParams = {
          ...(paramsObjForCards[inventory]?.[status]
            ? paramsObjForCards[inventory]?.[status]
            : {}),
          ...(paramsObjForCards[inventory]?.[priority]
            ? paramsObjForCards[inventory]?.[priority]
            : {}),
        };

        const dateParams = getDateRange({
          type: inventory,
          value: dateRange,
          basedOn: "Created Date",
        });

        const paginatedUrl = getPaginatedUrlWithSearchParams({
          pageNumber: 1,
          endpoint: url,
          limit: 1,
          searchParams: {
            ...searchParams,
            ...dateParams,
          },
        });

        const result: any = await crudService.getAll(paginatedUrl);
        return {
          count: result?.data?.meta?.total_count,
          ...keyToExtract,
        };
      });

      return await Promise.all(allPromises);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const dashboardCardSlice = createSlice({
  name: "cardsData",
  initialState: dashboardCardState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all dashboard card
    builder
      .addCase(fetchAllDashboardCardsData.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllDashboardCardsData.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.data = action.payload.sort((a: any, b: any) =>
            a.type.localeCompare(b.type)
          );
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllDashboardCardsData.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.meta = defaultMetaObj;
        }
      );

    // fetch leads type dashboard cards
    builder
      .addCase(fetchLeadsDashboardCardsData.pending, (state: any) => {
        state.leadsData.loading = true;
      })
      .addCase(
        fetchLeadsDashboardCardsData.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.leadsData.loading = false;
          state.leadsData.data = action.payload.sort((a: any, b: any) =>
            a.type.localeCompare(b.type)
          );
        }
      )
      .addCase(
        fetchLeadsDashboardCardsData.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.leadsData.loading = false;
          state.leadsData.error = action.payload as string;
        }
      );

    // fetch inventory type dashboard cards
    builder
      .addCase(fetchInventoryDashboardCardsData.pending, (state: any) => {
        state.inventoryData.loading = true;
      })
      .addCase(
        fetchInventoryDashboardCardsData.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.inventoryData.loading = false;
          state.inventoryData.data = action.payload.sort((a: any, b: any) =>
            a.type.localeCompare(b.type)
          );
        }
      )
      .addCase(
        fetchInventoryDashboardCardsData.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.inventoryData.loading = false;
          state.inventoryData.error = action.payload as string;
        }
      );
  },
});

export const dashboardCardsReducer = dashboardCardSlice.reducer;
